import * as babylon from "@babylonjs/core";
import lodash from "lodash";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { recolorMaterial } from "../../assetBuilders/materials";
import { BabylonState, ZERO_CAMERA_DELTAS } from "../../state";
import { computeThirdPersonZoom } from "../render/constrainCamera";
import {
  interpolationOf,
  type Interpolation,
} from "../render/setPositionsToTime";
import { targetFocus } from "../render/targetFocus";

export const ORBIT_DEFAULT_ALPHA_RAD = (45 * Math.PI) / 180;
export const ORBIT_DEFAULT_BETA_RAD = (75 * Math.PI) / 180;
export const ORBIT_DEFAULT_ZOOM = { min: 1, max: 100 } as const;
// Pad beta a sliver away from `0` and `π` to avoid the polar
// singularities where alpha becomes degenerate. Applied to both
// camera modes since both share the underlying `ArcRotateCamera`.
const BETA_LIMIT_PAD = 0.01;

export function updateSceneConfig(
  state: BabylonState,
  spec: schema.PanelConfig | undefined,
) {
  spec ??= {};
  syncCamera(state, spec.camera);
  syncGrid(state, spec.grid);
  syncDataSettings(state, spec.dataSettings);
  return state.sceneConfig;
}

/**
 * Resolves the panel's data settings into the `dataSettings` singleton.
 *
 * The one place a change to a data setting is observed, so anything built from
 * data under the old setting, and not re-read from the singleton every frame,
 * is restaled here.
 *
 * For the interpolation settings that is every entity's baked global
 * trajectory.
 * The per-frame samplers (`updatePositions`, `updateRotations`,
 * `updateVectorFields`, `poseJoints`) read the setting every frame and need
 * nothing. The bake does not: it sampled each link in the old mode at every
 * grid time where that link has no sample of its own (for a root, its own
 * rotation's sample times are enough), so under the new mode its trace would
 * no longer pass through the mesh drawn on it. Marking it stale has the next
 * reload poll rebake it, and that rebake restales the path trace and the
 * vector-field mesh baked from it. Every entity rather than the ones whose bake
 * would actually change: telling those apart costs a pass over every timeline,
 *
 * Restales only when the *resolved* settings sample differently (see
 * {@link samplesAlike}), so spelling out a default, clearing one, or editing
 * the threshold while interpolation is off rebakes nothing. The first
 * `setState` compares against an absent singleton, which `interpolationOf`
 * reads as the default; the entities it has just created are stale already
 * either way.
 */
function syncDataSettings(
  state: BabylonState,
  spec: schema.DataSettings | undefined,
) {
  spec ??= {};
  // Short-circuit if the config is unchanged.
  if (lodash.isEqual(spec, state.sceneConfig.dataSettings?.spec)) {
    return;
  }
  const before = interpolationOf(state);
  const settings = {
    spec,
    interpolationEnabled:
      spec.interpolationEnabled ??
      schema.DEFAULT_DATA_SETTINGS.interpolationEnabled,
    interpolationThresholdHz:
      spec.interpolationThresholdHz ??
      schema.DEFAULT_DATA_SETTINGS.interpolationThresholdHz,
  };
  state.sceneConfig.dataSettings = settings;
  if (samplesAlike(before, settings)) {
    return;
  }
  for (const entity of Object.values(state.entities)) {
    if (entity.globalPosition) {
      entity.globalPosition.cache.load.state = "stale";
    }
  }
}

/**
 * Whether two interpolation settings sample every cache alike: both hold every
 * pair, which interpolation being off does whatever the threshold, or both
 * blend the same pairs.
 */
function samplesAlike(a: Interpolation, b: Interpolation): boolean {
  if (!a.interpolationEnabled && !b.interpolationEnabled) {
    return true;
  }
  return (
    a.interpolationEnabled === b.interpolationEnabled &&
    a.interpolationThresholdHz === b.interpolationThresholdHz
  );
}

function syncCamera(state: BabylonState, spec: schema.Camera | undefined) {
  spec ??= {
    type: schema.CAMERA_TYPE.orbit,
    zoom: { min: 1, max: 100 },
    target: {
      id: Object.values(state.entities)[0]?.id ?? schema.DEFAULT_ENTITY_ID,
      mesh: Object.values(state.entities)[0]?.mesh ? "mesh" : "path",
    },
  };
  // Short-circuit if the config is unchanged.
  if (lodash.isEqual(spec, state.sceneConfig.camera?.spec)) {
    return;
  }

  let camera = state.sceneConfig.camera;
  if (camera && camera.spec.type !== spec.type) {
    // dispose the existing camera to make way for the newly requested type
    camera.babylonCamera.dispose();
    delete state.sceneConfig.camera;
    camera = undefined;
    // Reset the input accumulator
    state.input.cameraDeltas = { ...ZERO_CAMERA_DELTAS };
  }
  if (!camera) {
    camera =
      spec.type === schema.CAMERA_TYPE.thirdPerson
        ? buildThirdPersonCamera(state, spec)
        : buildOrbitCamera(state, spec);
    camera.babylonCamera.attachControl(state.context.canvas, true);
    state.sceneConfig.camera = camera;
  } else if (spec.type === schema.CAMERA_TYPE.thirdPerson) {
    applyThirdPersonSpec(state, camera, spec);
  }

  camera.babylonCamera.minZ = camera.zoom.min / 10;
  camera.babylonCamera.wheelDeltaPercentage = 0.01;
  camera.babylonCamera.inertia = 0.95;
  camera.babylonCamera.fov = 55 * (Math.PI / 180);
  // `babylonCamera.target` stays `Vector3.Zero()` for both modes:
  // `applyRenderOrigin` shifts every entity mesh by `-camera.focusPoint`
  // so the focus renders at the render-space origin. The orbit camera's
  // follow behavior comes from `updateCamera` writing `focusPoint` from
  // the tracked entity each frame, not from re-pinning the Babylon
  // target.
  camera.spec = spec;
}

/**
 * Moves a live third-person camera to where an edited spec says it should be.
 *
 * A third-person camera carries its whole framing in its spec — where it looks,
 * from what angle, and how far away — and `buildThirdPersonCamera` seeds all
 * four *at construction*. Without this, a spec edited while the camera already
 * exists was stored and never looked at again: the change survived a reload (it
 * is in the panel state) but did nothing to the viewport, which is the ingest
 * wizard's camera editor writing into a void.
 *
 * Only reached when the spec actually changed — `syncCamera` short-circuits on a
 * deeply equal one — which is what keeps this from fighting the user. Orbiting
 * and zooming write the *Babylon* camera and never the spec, so a re-render that
 * hands over an unchanged camera leaves the gesture alone; only a genuine edit
 * yanks the view, which is precisely what an edit is asking for.
 *
 * The pending input accumulator is dropped for the same reason. It holds the
 * tail of a gesture that is still decaying over the next few frames, and letting
 * it drain into a camera that has just been placed somewhere explicit would
 * slide it straight back off the mark.
 *
 * The orbit case has no equivalent: its spec holds a tracked entity and a zoom
 * envelope, both of which `updateCamera` and `constrainCamera` already re-read
 * from the spec every frame.
 */
function applyThirdPersonSpec(
  state: BabylonState,
  camera: NonNullable<BabylonState["sceneConfig"]["camera"]>,
  spec: schema.ThirdPersonCamera,
) {
  camera.babylonCamera.alpha = spec.alpha;
  camera.babylonCamera.beta = spec.beta;
  camera.babylonCamera.radius = spec.radius;
  // Written in place rather than replaced: `applyRenderOrigin` and the input
  // handlers hold this same vector and mutate it each frame.
  camera.focusPoint.set(
    spec.focusPoint.x,
    spec.focusPoint.y,
    spec.focusPoint.z,
  );
  state.input.cameraDeltas = { ...ZERO_CAMERA_DELTAS };
}

function buildOrbitCamera(
  state: BabylonState,
  spec: schema.OrbitCamera,
): NonNullable<BabylonState["sceneConfig"]["camera"]> {
  // TODO: it's possible to compute the zoom range based on the bounding box of the camera's target.
  // See https://doc.babylonjs.com/features/featuresDeepDive/mesh/displayBoundingBoxes/ for more
  const zoom = {
    min: spec.zoom.min ?? ORBIT_DEFAULT_ZOOM.min,
    max: spec.zoom.max ?? ORBIT_DEFAULT_ZOOM.max,
  };
  const babylonCamera = new babylon.ArcRotateCamera(
    `sceneConfig.camera`,
    ORBIT_DEFAULT_ALPHA_RAD,
    ORBIT_DEFAULT_BETA_RAD,
    zoom.min + (zoom.max - zoom.min) / 5,
    babylon.Vector3.Zero(),
    state.context.scene,
  );
  babylonCamera.lowerBetaLimit = BETA_LIMIT_PAD;
  babylonCamera.upperBetaLimit = Math.PI - BETA_LIMIT_PAD;
  // Seed `focusPoint` from whatever the target follows — the entity, or the
  // model node it names — if that is available at construction time.
  // `updateCamera`'s orbit branch overwrites this each frame from the same
  // function, so the seed only matters for the very first frame before the
  // render loop runs, and matching it to the steady state is what keeps that
  // frame from being a visible jump. If the entity isn't loaded yet,
  // `targetFocus` writes nothing and this stays at `Zero`, which is the origin
  // `applyRenderOrigin` would produce anyway. Absolute, so framing a child link
  // seeds the world position rather than its joint offset. `onSetState`
  // resolves the globals before calling this.
  const focusPoint = babylon.Vector3.Zero();
  targetFocus(state, spec.target, focusPoint);
  return { spec, zoom, babylonCamera, focusPoint };
}

function buildThirdPersonCamera(
  state: BabylonState,
  spec: schema.ThirdPersonCamera,
): NonNullable<BabylonState["sceneConfig"]["camera"]> {
  // Camera target stays at the render-space origin: `applyRenderOrigin`
  // shifts every entity by `-camera.focusPoint` each frame so the focus
  // renders at `(0,0,0)`. The camera is constructed pointing there and
  // stays there. `updateCamera` mutates `camera.focusPoint` from
  // pan/elevation input; the camera position is recomputed from
  // alpha/beta/radius around the unchanging render-space target.
  const babylonCamera = new babylon.ArcRotateCamera(
    `sceneConfig.camera`,
    spec.alpha,
    spec.beta,
    spec.radius,
    babylon.Vector3.Zero(),
    state.context.scene,
  );
  // Drop Babylon's default `ArcRotateCameraPointersInput` (and siblings).
  // App-owned `attachOrbitInput` / `attachPanInput` / `attachZoomInput`
  // write deltas into `state.input.cameraDeltas`; `updateCamera`
  // consumes the accumulator each frame. With Babylon's inputs cleared,
  // `_checkInputs` is a no-op and the camera fields move only via our
  // consume.
  babylonCamera.inputs.clear();
  babylonCamera.lowerBetaLimit = BETA_LIMIT_PAD;
  babylonCamera.upperBetaLimit = Math.PI - BETA_LIMIT_PAD;
  // Seed `focusPoint` from the persisted spec. `updateCamera` mutates
  // this directly from pan/elevation input; `applyRenderOrigin` reads
  // it each frame as the render origin. Clone rather than alias so
  // mutations don't bleed back into the persisted spec held by the
  // panel-state store.
  const focusPoint = new babylon.Vector3(
    spec.focusPoint.x,
    spec.focusPoint.y,
    spec.focusPoint.z,
  );
  // Seed the singleton zoom envelope from the same scene-derived
  // formula `constrainCamera` runs per frame. The first
  // `constrainCamera` pass will overwrite this, but the seed must be
  // sensible: `syncCamera` reads `camera.zoom.min` below to set `minZ`
  // (the near-clip plane), which would degenerate at zero.
  return {
    spec,
    zoom: computeThirdPersonZoom(state),
    babylonCamera,
    focusPoint,
  };
}

function syncGrid(state: BabylonState, spec: schema.Grid | undefined) {
  spec ??= {
    color: "#88888888",
    scale: 5,
    verticalOffset: 0,
  };
  // Short-circuit if the config is unchanged.
  if (lodash.isEqual(spec, state.sceneConfig.grid?.spec)) {
    return;
  }
  const grid = state.sceneConfig.grid;
  // If the grid size has not changed, then the pre-render system can reposition it and we don't have to rebuild the mesh.
  if (grid && spec.scale === grid.spec.scale) {
    if (spec.color !== grid.spec.color && grid?.material) {
      recolorMaterial(grid.material, spec.color);
    }
    grid.spec = spec;
    return;
  }

  // Otherwise, dispose the old grid — and the one it may still be retiring —
  // and make a new one ready to be loaded.
  if (grid) {
    grid.mesh?.dispose();
    grid.material?.dispose();
    grid.previous?.mesh.dispose();
    grid.previous?.material?.dispose();
    delete state.sceneConfig.grid;
  }

  state.sceneConfig.grid = {
    spec,
    verticalOffset: 0,
    load: {
      state: "stale",
    },
  };
}
